import { MantineTheme, CSSObject } from "@mantine/core";

/**
 * Creates style overrides for the aws-amplify components
 */
export const amplifyGlobalStyles = (theme: MantineTheme): CSSObject => ({
  '[class*="amplify"]': {
    all: "unset",
  },

  ".amplify-flex": {
    display: "flex",
    gap: theme.spacing.md,
  },

  ".amplify-visually-hidden": {
    position: "absolute",
    width: "1px",
    height: "1px",
    padding: 0,
    margin: "-1px",
    fill: "transparent",
    overflow: "hidden",
    clip: "rect(0, 0, 0, 0)",
    whiteSpace: "nowrap",
    borderWidth: 0,
  },

  // This is to specifically hide the Amplify "Resend Code" button on the signup confirmation
  '[data-amplify-authenticator-confirmsignup] .amplify-button:not([data-variation="primary"])': {
    display: "none",
  },

  ".amplify-button": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: theme.other.semanticColors.textInverse,
    background: theme.other.semanticColors.bg.brand,
    fontWeight: theme.other.fontWeights.semibold,
    padding: `${theme.spacing.sm}px ${theme.spacing.lg}px`,
    borderRadius: theme.radius.sm,
    fontFamily: theme.fontFamily,
    fontSize: theme.fontSizes.md,
    lineHeight: theme.lineHeight,
    "&:hover": {
      background: theme.other.semanticColors.bg.brandHover,
    },
    "&:active": {
      background: theme.other.semanticColors.bg.brandActive,
    },
    "&:focus-visible": {
      outline: `2px solid ${theme.other.semanticColors.borderFocus}`,
    },
    ".amplify-passwordfield &": {
      background: "unset",
      padding: "unset",
    },
  },

  ".amplify-label": {
    fontFamily: theme.fontFamily,
    fontWeight: theme.other.fontWeights.semibold,
    fontSize: theme.fontSizes.md,
    lineHeight: theme.lineHeight,
  },

  ".amplify-textfield": {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing.xs,
  },

  ".amplify-field-group": {
    alignItems: "center",
    borderRadius: theme.radius.sm,
    borderColor: theme.other.semanticColors.borderSubtle,
    borderWidth: "1px",
    borderStyle: "solid",
    gap: theme.spacing.xs,

    "&:has(:focus-visible)": {
      outline: `2px solid ${theme.other.semanticColors.borderFocus}`,
    },

    "&:has(:user-invalid)": {
      borderColor: theme.other.semanticColors.alertDangerBorder,
    },

    "&:has(.amplify-input)": {
      background: theme.other.semanticColors.bg.input,
      "&:hover, :active": {
        background: theme.other.semanticColors.bg.inputHover,
      },
    },
  },

  ".amplify-field-group__field-wrapper": {
    display: "flex",
    flexGrow: 1,
  },

  ".amplify-input": {
    flex: 1,
    padding: theme.spacing.sm,
    color: theme.other.semanticColors.textBold,
    fontFamily: theme.fontFamily,
    fontWeight: theme.other.fontWeights.normal,
    lineHeight: theme.lineHeight,
    borderRadius: theme.radius.sm,

    "&::placeholder": {
      color: theme.other.semanticColors.textSubtle,
    },

    ".amplify-passwordfield &": {
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
    },

    // Remove the number spin buttons from the confirmation code inputs
    // (the only number type inputs from amplify we use)
    "&[type='number']": {
      "-moz-appearance": "textfield",
    },
    "::-webkit-outer-spin-button, ::-webkit-inner-spin-button": {
      "-webkit-appearance": "none",
    },
  },

  ".amplify-button .amplify-icon": {
    display: "block",
    background: "transparent",
    padding: theme.spacing.sm,
    fontSize: theme.other.sizes["icon-md"],
    color: theme.other.semanticColors.icon.subtle,
    "&:hover, :active": {
      color: theme.other.semanticColors.icon.subtleHover,
    },
  },

  ".amplify-alert__icon": {
    display: "inline-flex",
    alignItems: "center",
    color: theme.other.semanticColors.alertDangerBorder,
    // this span has an inline style with width/height=1em,
    // so setting font size to control the size of the span
    fontSize: theme.other.sizes["icon-xs"],
  },

  ".amplify-alert--error, [data-amplify-sign-up-errors='true']": {
    display: "flex",
    gap: theme.spacing.xs,
    color: theme.other.semanticColors.textBold,
    background: theme.other.semanticColors.alertDangerBackground,
    padding: theme.spacing.xs,
    borderRadius: theme.radius.sm,
    fontFamily: theme.fontFamily,
    fontSize: theme.fontSizes.sm,
    fontWeight: theme.other.fontWeights.normal,
    lineHeight: theme.other.sizes["alert-content-lineheight"],
  },

  ".amplify-alert__dismiss": {
    display: "none",
  },

  ".amplify-loader": {
    display: "none",
  },

  ".amplify-tabs": {
    display: "none",
  },

  ".amplify-authenticator__subtitle": {
    display: "none",
  },
  ".amplify-heading": {
    display: "none",
  },
  ".amplify-button--link": {
    display: "none",
  },
});
